import React, { FC } from 'react';

import { PlusOutlined } from '@ant-design/icons';
import { Button, Empty, Flex, Layout, Row, Space } from 'antd';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { NSKeys } from '@app/i18n';
import { CatalogTab } from '@app/models/products';
import { useLocalSelector } from '@app/services/hooks/useRedux';
import { HeaderTitle } from '@components/HeaderTitle';
import { InfiniteScroll } from '@components/InfiniteScroll';
import { SkeletonImage } from '@components/products';
import { Card, CatalogActionMenu } from '@pages/Catalogs/components';
import { CardsContainer, ContentContainer } from '@pages/Catalogs/pages/List/styles';

import { useGetCatalogsQuery, useToggleCatalogPinQuery } from '../../hooks';

import { ListTypeSwitch } from './components/ListTypeSwitch';
import { ListType } from './interfaces';

export const List: FC = () => {
  const navigate = useNavigate();
  const catalogsT = useTranslation(NSKeys.catalogs);
  const [searchParams] = useSearchParams();
  const view = searchParams.get('view') as ListType | null;
  const activeTab = view || ListType.ACTIVE;
  const { data, hasNextPage, fetchNextPage, isLoading } = useGetCatalogsQuery({
    limit: 10,
    tab: activeTab === ListType.ARCHIVED ? CatalogTab.Archived : CatalogTab.Active,
  });
  const { mutate: toggleCatalogPin, isLoading: isPinLoading, variables: pinVariables } = useToggleCatalogPinQuery();

  const currentSession = useLocalSelector((state) => state.currentSession);

  const dataSource = React.useMemo(() => {
    return data?.pages?.map((data) => data.items || []).flat() || [];
  }, [data]);

  const handlePinClick =
    (catalogUid: string, isPinned: boolean): React.MouseEventHandler<HTMLElement> =>
    (event) => {
      event.stopPropagation();

      toggleCatalogPin({ catalogUid, isPinned });
    };

  const listContent = (
    <ContentContainer>
      <CardsContainer>
        {!isLoading && !dataSource.length && !hasNextPage && (
          <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} style={{ width: '100%', marginTop: 48 }} />
        )}
        {!!dataSource.length &&
          dataSource?.map((catalog) => (
            <Card
              image={catalog?.cover?.variants?.thumbnail?.url}
              key={catalog.uid}
              title={catalog.name}
              status={catalog.status}
              startDate={catalog.start_date}
              warehouseName={catalog?.warehouse?.name}
              isPinned={catalog.is_pinned}
              isPinLoading={isPinLoading && pinVariables?.catalogUid === catalog.uid}
              onPinClick={handlePinClick(catalog.uid, catalog.is_pinned)}
              onClick={() => navigate(`${catalog.uid}`)}
              extra={
                catalog?.$permissions?.view_settings && (
                  <CatalogActionMenu
                    catalogUid={catalog.uid}
                    catalogStatus={catalog.status}
                    canUpdate={catalog.$permissions.update}
                    type="link"
                    placement="bottomRight"
                  />
                )
              }
            />
          ))}
        {isLoading && <SkeletonImage size="m" />}
        {hasNextPage && (
          <InfiniteScroll key={data?.pages?.length || 0} next={fetchNextPage}>
            <SkeletonImage size="m" />
          </InfiniteScroll>
        )}
      </CardsContainer>
    </ContentContainer>
  );

  return (
    <Layout>
      <Layout.Header style={{ border: 0 }}>
        <Row justify="space-between" align="middle">
          <Flex gap={10} align="center">
            <HeaderTitle title={catalogsT.t('list.title')} />
            <ListTypeSwitch />
          </Flex>
          <Space size="middle">
            {!!currentSession?.productsContext?.scope?.$permissions?.create_catalogs && (
              <Button type="primary" icon={<PlusOutlined />} onClick={() => navigate('add')} size="middle">
                {catalogsT.t('list.addBtn')}
              </Button>
            )}
          </Space>
        </Row>
      </Layout.Header>
      <Layout.Content>{listContent}</Layout.Content>
    </Layout>
  );
};
